<script setup>
import { ref, computed, onMounted } from 'vue';
import StatCard from '../components/StatCard.vue';
import PaymentSummaryTable from '../components/PaymentSummaryTable.vue';
import PaymentDetailsTable from '../components/PaymentDetailsTable.vue';
import GlobalLoader from '../components/GlobalLoader.vue';

// ------------------- Reactive Data -------------------
const stats = ref([]);
const gameweekRows = ref([]);
const paymentRows = ref([]);
const isLoading = ref(false);

// ------------------- Column Definitions -------------------
const gameweekColumns = [
	{
		name: 'gameweek',
		label: 'Gameweek',
		field: 'gameweekRaw', // Use raw numeric value for sorting
		align: 'left',
		sortable: true,
	},
	{ name: 'totalEntries', label: 'Total Entries', field: 'totalEntries', align: 'center', sortable: true },
	{ name: 'paidEntries', label: 'Paid Entries', field: 'paidEntries', align: 'center', sortable: false },
	{ name: 'postalEntries', label: 'Postal Entries', field: 'postalEntries', align: 'center', sortable: false },
	{
		name: 'stripeRevenue',
		label: 'Stripe Revenue',
		field: 'stripeRevenueRaw', // Use raw numeric value for sorting
		align: 'center',
		sortable: true,
	},
	{ name: 'failedPayments', label: 'Failed Payments', field: 'failedPayments', align: 'center', sortable: true },
	{ name: 'successRate', label: 'Success Rate (%)', field: 'successRate', align: 'center', sortable: true }
];

const paymentColumns = [
	{ name: 'username', label: 'Username', field: 'username', align: 'left', sortable: true },
	{
		name: 'gameweek',
		label: 'Gameweek',
		field: 'gameweek',
		align: 'left',
		sortable: false,
	},
	{ name: 'paymentType', label: 'Payment Type', field: 'paymentType', align: 'left', sortable: false },
	{
		name: 'amount',
		label: 'Amount',
		field: 'amountRaw', // Use raw numeric value for sorting
		align: 'left',
		sortable: true
	},
	{ name: 'status', label: 'Status', field: 'status', align: 'left', sortable: false },
	{ name: 'transactionId', label: 'Transaction ID', field: 'transactionId', align: 'left', sortable: false },
	{
		name: 'date',
		label: 'Date',
		field: 'dateRaw', // Use raw timestamp for sorting
		align: 'left',
		sortable: true
	}
];

// ------------------- Pagination state for q-table -------------------
const summaryPagination = ref({
	sortBy: 'gameweek',
	descending: false,
	page: 1,
	rowsPerPage: 6
});

const paymentPagination = ref({
	sortBy: 'username',
	descending: false,
	page: 1,
	rowsPerPage: 6
});

// Sort handlers
const handleSummarySort = (props) => {
	summaryPagination.value = props.pagination;
};

const handlePaymentSort = (props) => {
	paymentPagination.value = props.pagination;
};

// ------------------- API Fetch Logic -------------------
const getReportsData = async () => {
	isLoading.value = true;
	try {
		const [gameweekRes, paymentRes, statsRes] = await Promise.all([
			fetch('/admin/reports/gameweek_summary'),
			fetch('/admin/reports/payment_details'),
			fetch('/admin/reports/statistics'),
		]);

		const [gameweekData, paymentData, statsData] = await Promise.all([
			gameweekRes.json(),
			paymentRes.json(),
			statsRes.json(),
		]);

		// Gameweek Summary
		if (gameweekData.success && Array.isArray(gameweekData.data)) {
			gameweekRows.value = gameweekData.data.map(item => {
				// Extract numeric value from gameweek string (e.g., "GW1" -> 1)
				const gameweekNum = parseInt(item.gameweek?.toString().replace(/\D/g, ''), 10) || 0;
				// Extract numeric value from stripeRevenue string (e.g., "€100.00" -> 100.00)
				const revenueStr = item.stripeRevenue?.toString().replace(/[€$£,\s]/g, '') || '0';
				const revenueNum = parseFloat(revenueStr) || 0;

				return {
					gameweek: item.gameweek,
					gameweekRaw: gameweekNum, // Raw numeric value for sorting
					totalEntries: item.totalEntries ?? 0,
					paidEntries: item.paidEntries ?? 0,
					postalEntries: item.postalEntries ?? 0,
					stripeRevenue: item.stripeRevenue ?? '€0.00',
					stripeRevenueRaw: revenueNum, // Raw numeric value for sorting
					failedPayments: item.failedPayments ?? 0,
					successRate: item.successRate ?? 0,
				};
			});
		}

		// Payment Details
		if (paymentData.success && Array.isArray(paymentData.data)) {
			paymentRows.value = paymentData.data.map(item => {
				const rawDate = item.date && item.date !== 'N/A' ? new Date(item.date) : null;
				return {
					username: item.username ?? '-',
					gameweek: item.gameweek ?? '-',
					paymentType: item.paymentType ?? '-',
					amount: item.amount ?? '€0.00',
					status: item.status ?? '-',
					transactionId: item.transactionId ?? 'N/A',
					date: rawDate ? rawDate.toLocaleString() : 'N/A',
					dateRaw: rawDate ? rawDate.getTime() : 0, // Store raw timestamp for sorting
					amountRaw: (() => {
						// Store raw numeric value for sorting
						const amountStr = item.amount ?? '€0.00';
						const numStr = amountStr.toString().replace(/[€$£,\s]/g, '');
						const num = parseFloat(numStr);
						return isNaN(num) ? 0 : num;
					})(),
				};
			});
		}

		// Stats
		if (statsData.success && statsData.data) {
			const s = statsData.data;
			stats.value = [
				{
					title: 'Total Paid Entries',
					value: s.totalPaidEntries ?? 0,
					icon: 'euro',
					color: 'green',
				},
				{
					title: 'Postal Entries',
					value: s.postalEntries ?? 0,
					icon: 'mail',
					color: 'blue',
				},
				{
					title: 'Stripe Revenue',
					value: s.stripeRevenue ?? '€0.00',
					icon: 'credit_card',
					color: 'purple',
				},
				{
					title: 'Failed Payments',
					value: s.failedPayments ?? 0,
					icon: 'block',
					color: 'red',
				},
			];
		}
	} catch (error) {
		console.error('Error fetching reports:', error);
	} finally {
		isLoading.value = false; // ✅ always turn off loading after everything
	}
};

// Fetch data on mount
onMounted(() => {
	getReportsData();
});
</script>

<template>
	<GlobalLoader :is-loading="isLoading" />
	<div class="bg-gray-50 min-h-screen">
		<main class="p-4 sm:p-6 lg:p-8">
			<!-- Stats Section -->
			<div class="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
				<StatCard v-for="stat in stats" :key="stat.title" :title="stat.title" :value="stat.value"
					:icon="stat.icon" :color="stat.color" />
			</div>

			<!-- Tables Section -->
			<div class="space-y-8">
				<PaymentSummaryTable :summary-column="gameweekColumns" :summary-row="gameweekRows"
					:pagination="summaryPagination" @update:pagination="handleSummarySort" />

				<PaymentDetailsTable :details-column="paymentColumns" :details-row="paymentRows"
					:pagination="paymentPagination" @update:pagination="handlePaymentSort" />
			</div>
		</main>
	</div>
</template>
